/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUser } from '@features/user';
import type {
  FormBodySchema3,
  FormOwnerScope2,
  FormResponse,
  FormWithRevision,
  GetV2ProviderFormsApiArg,
  PostV2ProviderFormsApiArg,
  PostV2ProviderFormsByFormIdCloneApiArg,
  PublishDraftResponse,
  SaveDraftResponse,
} from '@store/generated/forms';
import { useAppDispatch } from '@store/hooks';

import type { FormDefinition } from '../../../types/types';

import {
  formsV2Api,
  useCloneFormMutation,
  useCreateFormMutation,
  useDeleteFormDraftMutation,
  useDeleteFormMutation,
  useGetFormByIdQuery,
  useGetFormDraftQuery,
  useGetFormsQuery,
  useLazyFormBuilderWorkflowQuery,
  usePublishDraftMutation,
  useSaveDraftMutation,
} from './apiV2';

/**
 * Hook to list forms with filters
 */
export function useForms(
  args?: {
    scope?: FormOwnerScope2 | null;
    favorite?: boolean | null;
    tag?: string | null;
    search?: string | null;
    formType?: string | null;
    includeDrafts?: boolean | null;
    sortBy?: string | null;
    sortOrder?: string | null;
    page?: number;
    pageSize?: number;
  },
  options?: {
    skip?: boolean;
    pollingInterval?: number;
    refetchOnFocus?: boolean;
  },
) {
  const queryArgs: GetV2ProviderFormsApiArg = {
    scope: args?.scope,
    favorite: args?.favorite,
    tag: args?.tag,
    q: args?.search,
    formType: args?.formType,
    includeDrafts: args?.includeDrafts,
    sortBy: args?.sortBy,
    sortOrder: args?.sortOrder,
    page: args?.page ?? 1,
    pageSize: args?.pageSize ?? 20,
  };

  const result = useGetFormsQuery(queryArgs, {
    skip: options?.skip,
    pollingInterval: options?.pollingInterval || 0,
    refetchOnFocus: options?.refetchOnFocus ?? true,
  });

  return {
    forms: result.data?.items || [],
    page: result.data?.page || 1,
    perPage: result.data?.per_page || 20,
    total: result.data?.total || 0,
    totalPages: result.data?.total_pages || 0,
    isLoading: result.isLoading,
    isError: result.isError,
    isFetching: result.isFetching,
    error: result.error,
    refetch: result.refetch,
  };
}

/**
 * Hook to create a new form
 */
export function useCreateForm() {
  const [createForm, result] = useCreateFormMutation();

  const mutate = useCallback(
    (
      args: PostV2ProviderFormsApiArg['createFormRequest'],
      options?: { onSuccess?: (data: FormResponse) => void; onError?: (error: unknown) => void },
    ) => {
      const promise = createForm({ createFormRequest: args });

      if (options) {
        promise
          .unwrap()
          .then(data => options.onSuccess?.(data))
          .catch((error: unknown) => options.onError?.(error));
      }

      return promise;
    },
    [createForm],
  );

  return {
    mutate,
    createForm: mutate, // Alias for consistency
    isLoading: result.isLoading,
    isError: result.isError,
    isSuccess: result.isSuccess,
    error: result.error,
    data: result.data,
    reset: result.reset,
  };
}

/**
 * Hook to clone a form
 */
export function useCloneForm() {
  const [cloneForm, result] = useCloneFormMutation();

  const mutate = useCallback(
    (
      formId: string,
      args: PostV2ProviderFormsByFormIdCloneApiArg['cloneFormRequest'],
      options?: { onSuccess?: (data: FormResponse) => void; onError?: (error: unknown) => void },
    ) => {
      const promise = cloneForm({ formId, cloneFormRequest: args });

      if (options) {
        promise
          .unwrap()
          .then(data => options.onSuccess?.(data))
          .catch((error: unknown) => options.onError?.(error));
      }

      return promise;
    },
    [cloneForm],
  );

  return {
    mutate,
    cloneForm: mutate, // Alias for consistency
    isLoading: result.isLoading,
    isError: result.isError,
    isSuccess: result.isSuccess,
    error: result.error,
    data: result.data,
    reset: result.reset,
  };
}

/**
 * Hook to save a draft
 */
export function useSaveDraft() {
  const [saveDraft, result] = useSaveDraftMutation();

  const mutate = useCallback(
    (
      formId: string,
      body: FormBodySchema3,
      options?: {
        title?: string;
        description?: string | null;
        tags?: string[];
        onSuccess?: (data: SaveDraftResponse) => void;
        onError?: (error: unknown) => void;
      },
    ) => {
      const promise = saveDraft({
        formId,
        saveDraftRequest: {
          body,
          title: options?.title || 'Draft Form',
          description: options?.description,
          tags: options?.tags,
        },
      });

      if (options) {
        promise
          .unwrap()
          .then(data => options.onSuccess?.(data))
          .catch((error: unknown) => options.onError?.(error));
      }

      return promise;
    },
    [saveDraft],
  );

  return {
    mutate,
    saveDraft: mutate, // Alias for consistency
    isLoading: result.isLoading,
    isError: result.isError,
    isSuccess: result.isSuccess,
    error: result.error,
    data: result.data,
    reset: result.reset,
  };
}

/**
 * Hook to publish a draft
 */
export function usePublishDraft() {
  const [publishDraft, result] = usePublishDraftMutation();

  const mutate = useCallback(
    (
      formId: string,
      options?: {
        onSuccess?: (data: PublishDraftResponse) => void;
        onError?: (error: unknown) => void;
      },
    ) => {
      const promise = publishDraft({
        formId,
        publishDraftRequest: {},
      });

      if (options) {
        promise
          .unwrap()
          .then(data => options.onSuccess?.(data))
          .catch((error: unknown) => options.onError?.(error));
      }

      return promise;
    },
    [publishDraft],
  );

  return {
    mutate,
    publishDraft: mutate, // Alias for consistency
    isLoading: result.isLoading,
    isError: result.isError,
    isSuccess: result.isSuccess,
    error: result.error,
    data: result.data,
    reset: result.reset,
  };
}

/**
 * Hook for managing form drafts with autosave functionality
 */
export function useFormDraft(formId: string | null) {
  const { saveDraft, isLoading: isSaving } = useSaveDraft();
  const { publishDraft, isLoading: isPublishing } = usePublishDraft();

  const save = useCallback(
    (
      body: FormBodySchema3,
      options?: {
        title?: string;
        description?: string | null;
        tags?: string[];
        onSuccess?: () => void;
        onError?: (error: unknown) => void;
      },
    ) => {
      if (!formId) {
        options?.onError?.(new Error('No form ID provided'));
        return;
      }
      return saveDraft(formId, body, options);
    },
    [formId, saveDraft],
  );

  const publish = useCallback(
    (options?: { onSuccess?: () => void; onError?: (error: unknown) => void }) => {
      if (!formId) {
        options?.onError?.(new Error('No form ID provided'));
        return;
      }
      return publishDraft(formId, options);
    },
    [formId, publishDraft],
  );

  return {
    save,
    publish,
    isSaving,
    isPublishing,
    isProcessing: isSaving || isPublishing,
  };
}

/**
 * Hook to delete a form
 */
export function useDeleteFormV2() {
  const [mutate, result] = useDeleteFormMutation();

  const deleteForm = useCallback(
    (
      formId: string,
      options?: {
        onSuccess?: () => void;
        onError?: (error: unknown) => void;
      },
    ) => {
      return mutate({ formId })
        .unwrap()
        .then(result => {
          options?.onSuccess?.();
          return result;
        })
        .catch((error: unknown) => {
          options?.onError?.(error);
          throw error;
        });
    },
    [mutate],
  );

  return {
    deleteForm,
    isLoading: result.isLoading,
    isError: result.isError,
    isSuccess: result.isSuccess,
    error: result.error,
  };
}

/**
 * Hook to discard a form draft
 */
export function useDiscardDraft() {
  const [mutate, result] = useDeleteFormDraftMutation();

  const discardDraft = useCallback(
    (
      formId: string,
      options?: {
        onSuccess?: () => void;
        onError?: (error: unknown) => void;
      },
    ) => {
      return mutate({ formId })
        .unwrap()
        .then(result => {
          options?.onSuccess?.();
          return result;
        })
        .catch((error: unknown) => {
          options?.onError?.(error);
          throw error;
        });
    },
    [mutate],
  );

  return {
    discardDraft,
    isLoading: result.isLoading,
    isError: result.isError,
    isSuccess: result.isSuccess,
    error: result.error,
  };
}

/**
 * Hook to upload a file and poll for form generation
 * Uses direct fetch to bypass RTK Query FormData issues
 */

export function useFormBuilderFileUpload() {
  const [isUploading, setIsUploading] = useState(false);
  const [workflowTaskId, setWorkflowTaskId] = useState<string | null>(null);
  const [completedFormId, setCompletedFormId] = useState<string | null>(null);
  const [workflowError, setWorkflowError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<any>(null);
  const { getAccessToken } = useUser();
  const dispatch = useAppDispatch();
  const [triggerWorkflowPoll] = useLazyFormBuilderWorkflowQuery();
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const currentTaskIdRef = useRef<string | null>(null);

  const uploadFile = useCallback(
    async (file: File, formId?: string | null) => {
      console.warn('🪝 useFormBuilderFileUpload uploadFile called with:', {
        fileName: file.name,
        fileSize: file.size,
        formId,
      });

      try {
        setIsUploading(true);
        setWorkflowError(null);
        setCompletedFormId(null);
        setUploadError(null);

        // Create FormData
        const formData = new FormData();
        formData.append('file', file, file.name);

        // Add form_id if provided
        if (formId) {
          formData.append('form_id', formId);
          console.warn('📁 Added form_id to FormData:', formId);
        }

        console.warn('📁 FormData created with file:', file.name);

        // Get access token using the user hook
        let accessToken: string | null = null;

        try {
          accessToken = await getAccessToken();
        } catch (tokenError) {
          console.warn('Could not get access token:', tokenError);
        }

        // Make direct fetch request
        const apiUrl = import.meta.env.VITE_API_URL || 'https://api.smartscribe.com';
        const uploadUrl = `${apiUrl}/v2/provider/forms/form-builder/upload`;

        const headers: HeadersInit = {};
        if (accessToken) {
          headers['Authorization'] = `Bearer ${accessToken}`;
        }
        // Don't set Content-Type - let browser set it for FormData

        console.warn('🌐 Making direct fetch to:', uploadUrl);

        const response = await fetch(uploadUrl, {
          method: 'POST',
          body: formData,
          headers,
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Upload failed (${response.status}): ${errorText}`);
        }

        const uploadResponse: {
          workflow_task_id: string;
          document_id: string;
          message: string;
        } = await response.json();
        console.warn('🪝 Upload response received:', uploadResponse);

        // Step 2: Start polling workflow
        console.warn('🪝 Starting workflow polling with taskId:', uploadResponse.workflow_task_id);
        currentTaskIdRef.current = uploadResponse.workflow_task_id;
        setWorkflowTaskId(uploadResponse.workflow_task_id);
      } catch (error) {
        console.error('❌ Upload failed in hook:', error);
        setUploadError(error);
        throw error;
      } finally {
        setIsUploading(false);
      }
    },
    [getAccessToken],
  );

  // Stop polling helper
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    currentTaskIdRef.current = null;
  }, []);

  // Handle workflow polling with manual control
  useEffect(() => {
    if (!workflowTaskId || !currentTaskIdRef.current) return;

    // Clear any existing interval first
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    const taskId = currentTaskIdRef.current;
    
    // Poll function
    const poll = async () => {
      // Double-check we still have a valid taskId
      if (!currentTaskIdRef.current) return;
      
      try {
        const result = await triggerWorkflowPoll({ workflowTaskId: taskId }).unwrap();

        if (result.is_complete) {
          stopPolling();

          if (result.is_success) {
            // The form_id is nested in result.result.form_id
            const workflowData = result as any;
            const formId = workflowData.result?.result?.form_id || workflowData.form_id;
            const processingComplete = workflowData.result?.result?.processing_complete;
            const message = workflowData.result?.result?.message as string | undefined;

            // Check if processing actually failed despite is_success being true
            if (processingComplete === false || message?.toLowerCase().includes('failed')) {
              console.error('❌ Form processing failed despite success status:', result);
              setWorkflowError('Unable to process this file. Please try a different file type.');
            } else if (formId) {
              console.warn('🎯 Form generation completed with form_id:', formId);
              setCompletedFormId(formId as string);

              // Invalidate the form draft cache to trigger automatic refetch
              dispatch(
                formsV2Api.util.invalidateTags([
                  // FormBuilder tags for the form builder UI
                  { type: 'FormBuilder' as any, id: formId as string },
                  { type: 'FormBuilderDraft' as any, id: formId as string },
                  // Original tags for other consumers
                  { type: 'FormDraft', id: formId as string },
                  { type: 'FormV2', id: formId as string },
                ]),
              );
            } else {
              console.error(
                '❌ Form generation completed but no form_id found in response:',
                result,
              );
              setWorkflowError('Form generation completed but no form ID returned');
            }
          } else {
            const workflowData = result as any;
            setWorkflowError((workflowData.error as string) || 'Form generation failed');
          }
        }
        // If not complete, continue polling via interval
      } catch (error) {
        // Handle network errors
        console.error('Polling error:', error);
      }
    };

    // Initial poll
    poll();

    // Set up polling interval
    pollingIntervalRef.current = setInterval(() => {
      void poll();
    }, 2000);

    // Cleanup on unmount
    return () => {
      stopPolling();
    };
  }, [workflowTaskId, triggerWorkflowPoll, stopPolling, dispatch]);

  const reset = useCallback(() => {
    stopPolling();
    setWorkflowTaskId(null);
    setCompletedFormId(null);
    setWorkflowError(null);
    setUploadError(null);
    setIsUploading(false);
  }, [stopPolling]);

  return {
    uploadFile,
    isUploading,
    isProcessing: !!workflowTaskId && !completedFormId && !workflowError,
    workflowStatus: undefined, // No longer tracking via query result
    completedFormId,
    error: uploadError || workflowError,
    reset,
  };
}

/**
 * Hook to generate a form from text description and poll for form generation
 * Uses the text generation endpoint and reuses the workflow polling
 */
export function useFormBuilderTextGeneration() {
  const { usePostV2ProviderFormsFormBuilderGenerateFromTextMutation } = formsV2Api;
  const [generateFromText, generateResult] =
    usePostV2ProviderFormsFormBuilderGenerateFromTextMutation();
  const [workflowTaskId, setWorkflowTaskId] = useState<string | null>(null);
  const [completedFormId, setCompletedFormId] = useState<string | null>(null);
  const [workflowError, setWorkflowError] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const [triggerWorkflowPoll] = useLazyFormBuilderWorkflowQuery();
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const currentTaskIdRef = useRef<string | null>(null);

  const generateForm = useCallback(
    async (textPrompt: string, formId?: string | null) => {
      console.warn('🤖 useFormBuilderTextGeneration generateForm called with:', {
        textPrompt: textPrompt.substring(0, 50) + '...',
        formId,
      });

      try {
        setWorkflowError(null);
        setCompletedFormId(null);

        // Call the text generation mutation
        const response = await generateFromText({
          formBuilderTextGenerationRequest: {
            form_text_prompt: textPrompt,
            form_id: formId,
          },
        }).unwrap();

        console.warn('🤖 Text generation response received:', response);

        // Start polling workflow
        console.warn('🤖 Starting workflow polling with taskId:', response.workflow_task_id);
        currentTaskIdRef.current = response.workflow_task_id;
        setWorkflowTaskId(response.workflow_task_id);
      } catch (error) {
        console.error('❌ Text generation failed in hook:', error);
        setWorkflowError(
          error instanceof Error ? error.message : 'Failed to generate form from text',
        );
        throw error;
      }
    },
    [generateFromText],
  );

  // Stop polling helper
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    currentTaskIdRef.current = null;
  }, []);

  // Handle workflow polling with manual control
  useEffect(() => {
    if (!workflowTaskId || !currentTaskIdRef.current) return;

    // Clear any existing interval first
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    const taskId = currentTaskIdRef.current;
    
    // Poll function
    const poll = async () => {
      // Double-check we still have a valid taskId
      if (!currentTaskIdRef.current) return;
      
      try {
        const result = await triggerWorkflowPoll({ workflowTaskId: taskId }).unwrap();

        if (result.is_complete) {
          stopPolling();

          if (result.is_success) {
            // The form_id is nested in result.result.form_id (same as file upload)
            const workflowData = result as any;
            const formId = workflowData.result?.result?.form_id || workflowData.form_id;
            const processingComplete = workflowData.result?.result?.processing_complete;
            const message = workflowData.result?.result?.message as string | undefined;

            // Check if processing actually failed despite is_success being true
            if (processingComplete === false || message?.toLowerCase().includes('failed')) {
              console.error('❌ Form processing failed despite success status:', result);
              setWorkflowError('Unable to process this file. Please try a different file type.');
            } else if (formId) {
              console.warn('🎯 Form generation from text completed with form_id:', formId);
              setCompletedFormId(formId as string);

              // Invalidate the form draft cache to trigger automatic refetch
              dispatch(
                formsV2Api.util.invalidateTags([
                  // FormBuilder tags for the form builder UI
                  { type: 'FormBuilder' as any, id: formId as string },
                  { type: 'FormBuilderDraft' as any, id: formId as string },
                  // Original tags for other consumers
                  { type: 'FormDraft', id: formId as string },
                  { type: 'FormV2', id: formId as string },
                ]),
              );
            } else {
              console.error(
                '❌ Form generation from text completed but no form_id found in response:',
                result,
              );
              setWorkflowError('Form generation completed but no form ID returned');
            }
          } else {
            const workflowData = result as any;
            setWorkflowError((workflowData.error as string) || 'Form generation failed');
          }
        }
        // If not complete, continue polling via interval
      } catch (error) {
        // Handle network errors
        console.error('Polling error:', error);
      }
    };

    // Initial poll
    poll();

    // Set up polling interval
    pollingIntervalRef.current = setInterval(() => {
      void poll();
    }, 2000);

    // Cleanup on unmount
    return () => {
      stopPolling();
    };
  }, [workflowTaskId, triggerWorkflowPoll, stopPolling, dispatch]);

  const reset = useCallback(() => {
    stopPolling();
    setWorkflowTaskId(null);
    setCompletedFormId(null);
    setWorkflowError(null);
    generateResult.reset();
  }, [generateResult, stopPolling]);

  return {
    generateForm,
    isGenerating: generateResult.isLoading,
    isProcessing: !!workflowTaskId && !completedFormId && !workflowError,
    workflowStatus: undefined, // No longer tracking via query result
    completedFormId,
    error: generateResult.error || workflowError,
    reset,
  };
}

/**
 * Adapter function to transform V2 FormWithRevision to legacy FormDefinition
 * This maintains compatibility with existing FormPreview component
 */
function transformV2FormToLegacy(formWithRevision: FormWithRevision): FormDefinition {
  const { form, current_revision } = formWithRevision;

  return {
    form_id: form.id || '',
    title: current_revision?.title || 'Untitled Form',
    description: current_revision?.description || '',
    validationSchema: undefined, // Not used in preview
    groups: current_revision?.body?.groups || [],
  };
}

/**
 * Hook to get a single form by ID with V2 to legacy transformation
 */
export function useFormById(
  formId: string | null,
  options?: {
    version?: number | null;
    skip?: boolean;
    pollingInterval?: number;
    refetchOnFocus?: boolean;
  },
) {
  const result = useGetFormByIdQuery(
    { formId: formId || '', version: options?.version },
    {
      skip: !formId || options?.skip,
      pollingInterval: options?.pollingInterval || 0,
      refetchOnFocus: options?.refetchOnFocus ?? true,
    },
  );

  // Memoize the transformed form to prevent infinite re-renders
  const transformedForm = useMemo(() => {
    return result.data?.form ? transformV2FormToLegacy(result.data.form) : null;
  }, [result.data?.form]);

  return {
    form: transformedForm,
    isLoading: result.isLoading,
    isError: result.isError,
    isFetching: result.isFetching,
    error: result.error,
    refetch: result.refetch,
  };
}

/**
 * Hook to get a form draft by ID with V2 to legacy transformation
 */
export function useFormDraftById(
  formId: string | null,
  options?: {
    skip?: boolean;
    pollingInterval?: number;
    refetchOnFocus?: boolean;
  },
) {
  const result = useGetFormDraftQuery(
    { formId: formId || '' },
    {
      skip: !formId || options?.skip,
      pollingInterval: options?.pollingInterval || 0,
      refetchOnFocus: options?.refetchOnFocus ?? true,
    },
  );

  // Memoize the transformed form to prevent infinite re-renders
  const transformedForm = useMemo(() => {
    if (result.data) {
      // Draft response has the structure { form: { form: {...}, current_revision: {...} } }
      const draftData = result.data as any;
      return draftData.form ? transformV2FormToLegacy(draftData.form as FormWithRevision) : null;
    }
    return null;
  }, [result.data]);

  return {
    form: transformedForm,
    isLoading: result.isLoading,
    isError: result.isError,
    isFetching: result.isFetching,
    error: result.error,
    refetch: result.refetch,
  };
}
